/**
 * useTrialUnlockEligibility (Monetization Step 8: two-phase trial)
 *
 * Eligibility gate (R8.5) for the deferred-trial "unlock free until [date]"
 * card-entry surface (TrialUnlockModal). The two-phase trial is the DEFAULT and
 * ONLY flow now: there is no PostHog experiment. Every in-flight cardless trial
 * gets the deferred-trial CTA instead of the instant-pay checkout.
 *
 * `eligible === true` requires exactly one condition:
 *   - The user is currently `subscription_status === 'trial_active'`, an
 *     in-flight cardless trial (R8.5). A `trial_expired` / `Free` / grandfather
 *     user (including one who cancelled a carded trial) does NOT qualify: they
 *     fall through to the existing instant-pay CTA and must never be offered a
 *     second deferred trial. The gate is on the CURRENT status, so a re-add
 *     after a carded cancel routes to instant-pay (Error Handling scenario 9).
 *
 * @returns {{ eligible: boolean }}
 *   - eligible: render the deferred-trial (TrialUnlockModal) surface
 */

import { useTier } from '../context/TierContext.js';

// R8.5: the deferred-trial CTA is offered ONLY to an in-flight cardless trial.
// A single source of truth so the gate stays exact-match (no .neq drift).
export const isDeferredTrialEligibleStatus = (subscriptionStatus) =>
  subscriptionStatus === 'trial_active';

/**
 * Pure helper so the gate is unit-testable without React. Returns true only
 * when the status is an in-flight cardless trial. (No experiment arm: the
 * two-phase trial is the default + only flow.)
 */
export const resolveTrialUnlockEligible = ({ subscriptionStatus }) =>
  isDeferredTrialEligibleStatus(subscriptionStatus);

export const useTrialUnlockEligibility = () => {
  const { subscriptionStatus } = useTier();

  const eligible = resolveTrialUnlockEligible({ subscriptionStatus });

  return { eligible };
};

export default useTrialUnlockEligibility;
